import { useState, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import syndicationApi from 'src/utils/syndicationApi';
import {
    Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
    AppBar, Toolbar, Typography, IconButton, Avatar, Menu, MenuItem,
    Divider, useMediaQuery, useTheme,
} from '@mui/material';
import { Icon } from '@iconify/react';
import briefcaseFill from '@iconify/icons-eva/briefcase-fill';
import pieChartFill from '@iconify/icons-eva/pie-chart-fill';
import personFill from '@iconify/icons-eva/person-fill';
import settings2Fill from '@iconify/icons-eva/settings-2-fill';
import logOutFill from '@iconify/icons-eva/log-out-fill';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import homeFill from '@iconify/icons-eva/home-fill';

const DRAWER_WIDTH = 260;

const RootStyle = styled('div')({
    display: 'flex',
    minHeight: '100vh',
});

const MainStyle = styled('main')(({ theme }) => ({
    flexGrow: 1,
    overflow: 'auto',
    minHeight: '100vh',
    paddingTop: 72,
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
        marginLeft: DRAWER_WIDTH,
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
    },
}));

const NAV_ITEMS = [
    { title: 'Dashboard', path: '/investor/dashboard', icon: homeFill },
    { title: 'My Deals', path: '/investor/deals', icon: briefcaseFill },
    { title: 'Portfolio', path: '/investor/portfolio', icon: pieChartFill },
    { title: 'Profile', path: '/investor/profile', icon: personFill },
    { title: 'Settings', path: '/investor/settings', icon: settings2Fill },
];

export default function InvestorLayout() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    const user = JSON.parse(localStorage.getItem('investorUser') || '{}');

    const handleLogout = useCallback(async () => {
        try {
            await syndicationApi.post('investor/profile/logout/');
        } catch (_) {
            // Ignore — token may already be expired
        }
        localStorage.removeItem('investorAuthToken');
        localStorage.removeItem('investorUser');
        localStorage.removeItem('activeRole');
        navigate('/investor/login');
    }, [navigate]);

    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>GX</Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Gateway X</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Investor Portal</Typography>
                </Box>
            </Box>
            <Divider />
            <List sx={{ px: 1.5, pt: 2, flex: 1 }}>
                {NAV_ITEMS.map((item) => (
                    <ListItemButton
                        key={item.path}
                        onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                        sx={{
                            borderRadius: 1.5, mb: 0.5, py: 1,
                            ...(location.pathname.startsWith(item.path) && {
                                bgcolor: 'action.selected',
                                color: 'primary.main',
                                '& .MuiListItemIcon-root': { color: 'primary.main' },
                            }),
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <Icon icon={item.icon} width={22} height={22} />
                        </ListItemIcon>
                        <ListItemText primary={item.title} primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                    </ListItemButton>
                ))}
            </List>
        </Box>
    );

    return (
        <RootStyle>
            <AppBar position="fixed" sx={{
                bgcolor: '#fff', color: 'text.primary', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, ml: { md: `${DRAWER_WIDTH}px` },
            }}>
                <Toolbar>
                    {isMobile && (
                        <IconButton onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
                            <Icon icon={menu2Fill} width={24} height={24} />
                        </IconButton>
                    )}
                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
                        {NAV_ITEMS.find(i => location.pathname.startsWith(i.path))?.title || 'Investor Portal'}
                    </Typography>
                    <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: '#1a237e', fontSize: 14 }}>
                            {(user.first_name || 'I')[0]}
                        </Avatar>
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
                        PaperProps={{ sx: { width: 200, mt: 1 } }}>
                        <MenuItem disabled>
                            <Typography variant="body2" noWrap>{user.email || 'Investor'}</Typography>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { setAnchorEl(null); navigate('/investor/profile'); }}>
                            <ListItemIcon><Icon icon={personFill} width={20} /></ListItemIcon>
                            Profile
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorEl(null); handleLogout(); }}>
                            <ListItemIcon><Icon icon={logOutFill} width={20} /></ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Sidebar */}
            {isMobile ? (
                <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}
                    PaperProps={{ sx: { width: DRAWER_WIDTH } }}>
                    {drawerContent}
                </Drawer>
            ) : (
                <Drawer variant="permanent" PaperProps={{ sx: { width: DRAWER_WIDTH, borderRight: '1px solid', borderColor: 'divider' } }}>
                    {drawerContent}
                </Drawer>
            )}

            <MainStyle>
                <Outlet />
            </MainStyle>
        </RootStyle>
    );
}
